import { v4 as uuid } from "uuid";
import { Plugin, PluginKey } from "prosemirror-state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import Image from "@tiptap/extension-image";
import Component from "./node";
import InlineImage from "./inline-image";

import imageStore from "@app/state/store/report-document/image";
import documentStore from "@app/state/store/report-document/document";
import isInFootnote from "@app/components/report-document/editor/modules/table/utils/is-in-footnote";
import { inList } from "@app/components/report-document/editor/utils";

import tr from "../utils/transaction";

const ImageNode = Image.extend({
    group: "block",
    allowBase64: true,
    inline: false,

    // Also reaches the vendored editor, which only takes known modules.
    addExtensions() {
        return [InlineImage];
    },

    onBeforeCreate() {
        const readonly = !this.editor.options.editable;
        this.options.readonly = readonly;
        this.type.spec.selectable = !readonly;

        this.storage.store = this.options.store || {};
    },

    onTransaction(args) {
        let update = false;
        const editor = args.editor;

        if (!tr.isEdit(args)) {
            return;
        }

        const added = tr.addedNodes(args, { type: this.name });
        if (added.length) {
            update = true;
        }

        const removed = tr.removedNodes(args, { type: this.name });
        if (removed.length) {
            update = true;
        }

        if (update && editor.sectionId) {
            const content = editor.getJSON();
            documentStore.processSection(editor.sectionId, content);
        }
    },

    addOptions() {
        // lodash debounce allows only for 1 call at a time, so we need to use a map to store the pending calls if we have multiple images in content that need to be processed
        const pendingFormatCalls = new Map();

        return {
            readonly: false,
            store: {},
            metadata: {},
            processImage: async (id, image, cb) => {
                const imageKey = `${id}:${image.type}:${image.value}`;

                try {
                    let formatPromise;

                    if (pendingFormatCalls.has(imageKey)) {
                        formatPromise = pendingFormatCalls.get(imageKey);
                    } else {
                        formatPromise = imageStore.formatImage(image);
                        pendingFormatCalls.set(imageKey, formatPromise);
                    }

                    const newImage = await formatPromise;

                    pendingFormatCalls.delete(imageKey);

                    cb(newImage);
                } catch (error) {
                    pendingFormatCalls.delete(imageKey);
                    cb(null);
                }
            },
        };
    },

    addStorage() {
        return {
            store: {},
        };
    },

    addAttributes() {
        return {
            id: {
                default: "",
                parseHTML: (image) => {
                    if (!image.hasAttribute("data-diff-mode") || !image.hasAttribute("id")) {
                        return uuid();
                    }

                    return image.getAttribute("id");
                },
            },

            processed: {
                default: false,
            },

            file: {
                default: null,
                parseHTML: (image) => {
                    return image.getAttribute("file") || null;
                },
            },

            src: {
                default: null,
                parseHTML: (image) => {
                    return image.getAttribute("src") || null;
                },
            },

            width: {
                renderHTML: (attributes) => {
                    return {
                        width: attributes.width,
                    };
                },
            },

            height: {
                renderHTML: (attributes) => {
                    return {
                        height: attributes.height,
                    };
                },
            },
            caption: {
                default: "",
            },
            invalid: {
                default: false,
            },
            "data-diff-node": {
                default: undefined,
            },
            "data-diff-id": {
                default: undefined,
                parseHTML: (img) => {
                    if (img.hasAttribute("data-diff-node")) {
                        return uuid();
                    }

                    return undefined;
                },
            },
            comment: {
                default: undefined,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: "img[src]",
            },
            {
                tag: "img[file]",
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ["img", HTMLAttributes];
    },

    addNodeView() {
        return ReactNodeViewRenderer(Component);
    },

    addCommands() {
        return {
            /**
             * Adds a new image entry to the content
             */
            addImageEntry: (options) => (api) => {
                if (isInFootnote(api)) {
                    return false;
                }

                const { commands, editor } = api;

                if (options) {
                    const content = [
                        {
                            type: this.name,
                            attrs: {
                                id: uuid(),
                                ...options.entry,
                            },
                        },
                    ];

                    // add space if inside a list
                    if (inList(editor)) {
                        content.push({
                            type: "text",
                            text: " ",
                        });
                    }

                    commands.insertContent(content);
                }

                return true;
            },

            /**
             * Show an image picker
             */
            showImagePicker: () => async ({ editor }) => {
                editor.emit("imagePicker");
            },
            editImageCaption: () => ({ editor }) => {
                if (editor.state.selection) {
                    const node = editor.state.selection.node;
                    if (node?.type?.name === this.name) {
                        return true;
                    }
                }

                return false;
            },
        };
    },

    marks: "commentMark",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("imagePasteHandler"),
                props: {
                    transformPastedHTML: (html) => {
                        if (html.includes("<img ")) {
                            const div = document.createElement("div");
                            div.innerHTML = html;

                            const images = div.querySelectorAll("img");
                            for (const image of images) {
                                image.removeAttribute("id");
                                // If the image has a src attribute, mark it as unprocessed
                                // so it will be re-uploaded to get a file reference
                                if (image.getAttribute("src")) {
                                    image.setAttribute("processed", "false");
                                    // Clear the file attribute to ensure it gets processed
                                    image.removeAttribute("file");
                                }
                            }

                            html = div.innerHTML;
                            div.remove();
                        }

                        return html;
                    },
                },
            }),
        ];
    },
});

export default ImageNode;
